import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Platform } from "react-native"
import { useAsyncSelector } from "src/hooks/useAsyncSelector"
import { useRefresher } from "src/hooks/useRefresher"
import { inboxContactsRequestIdState } from "src/recoil/atoms/contact/inboxContactsRequestIdState"
import { selectedMyOrganizationSelector } from "src/recoil/selectors/organization/selectedMyOrganizationSelector"
import { useContactDetailDataRefresher } from "src/recoil/hooks/refresher/useContactDetailDataRefresher"
import { useFetcher } from "src/hooks/useFetcher"
import { CustomAlert } from "src/utils/CustomAlert"
import { useRequestData } from "src/recoil/hooks/useRequestData"
import { updateFavoriteContactInbox } from "src/apis/contact/updateFavoriteContactInbox"
import { useResetter } from "./contact/create/useResetter"
import { ContactFilterValue } from "src/types/contact/ContactFilter"
import { myProfileSelectorFamily } from "src/recoil/selectors/organization/myProfile/myProfileSelectorFamily"
import { trashContact } from "src/apis/contact/trashContact"
import { useBottomTabBadgeData } from "src/recoil/hooks/bottomTab/useBottomTabBadgeData"
import { unreadThreadCountRequestIdState } from "src/recoil/atoms/contact/unreadThreadCountRequestIdState"
import { inboxContactsConcatSelectorFamily } from "src/recoil/selectors/contact/inboxContactsConcatSelectorFamily"
import { inboxContactsConcatRequestIdState } from "src/recoil/atoms/contact/inboxContactsConcatRequestIdState"
import { contactReplyThreadsRequestIdState } from "src/recoil/atoms/contact/contactReplyThreadsRequestIdState"
import { hasUnreadThreadMessageRequestIdState } from "src/recoil/atoms/contact/hasUnreadThreadMessageRequestIdState"
import { useRecoilState, useRecoilValue, useResetRecoilState } from "recoil"
import { inboxContactFavoriteFlgsCacheState } from "src/recoil/atoms/contact/contactFavoriteFlgsCacheState"
import { wait } from "src/utils/wait"
import { defaultContactFilterState } from "src/recoil/atoms/contact/defaultContactFilterState"
import { GetInboxContactsSuccess } from "src/apis/contact/getInboxContacts"
import { requestDataSelector } from "src/recoil/selectors/auth/requestDataSelector"
import { organizationDetailSelectorFamily } from "src/recoil/selectors/organization/organizationDetailSelectorFamily"
import { organizationRefreshState } from "src/recoil/atoms/organization/organizationRefreshPermissionState"
import { organizationDetailRequestIdState } from "src/recoil/atoms/organization/organizationDetailRequestIdState"
import { ContactListCacheService } from "src/services/cache/ContactListCacheService"
import { teamPermissionRequestIdState } from "src/recoil/atoms/organization/teamPermissionRequestIdState"
import { useIncrementalUnreadCount } from "src/hooks/useIncrementalUnreadCount"
import { UnreadReplyCountCacheService } from "src/services/cache/UnreadReplyCountCacheService"
import { getUnreadThreadCount } from "src/apis/contact/getUnreadThreadCount"
import { UnreadContactCountCacheService } from "src/services/cache/UnreadContactCountCacheService"
import { OptimisticStateService } from "src/services/cache/OptimisticStateService"
import { useForegroundRefresh } from "src/hooks/useForegroundRefresh"
import { checkLatestInboxContact } from "src/apis/contact/checkLatestInboxContact"
import { tokensState } from "src/recoil/atoms/authorization/tokensState"
import { getIsAppJustLaunched, clearAppJustLaunchedFlag } from "src/hooks/useAppLaunchCacheClear"

export const useInboxData = () => {
  const [searchText, setSearchText] = useState("")
  const [confirmedSearchText, setConfirmedSearchText] = useState("")
  const [filters, setFilters] = useState<ContactFilterValue[]>([])
  const [defaultFilters, setDefaultFilters] = useRecoilState(defaultContactFilterState)
  useEffect(() => {
    if (defaultFilters != null) {
      // 非同期で実行してレンダリング中のstate更新を回避
      setTimeout(() => {
        setFilters(defaultFilters)
        setDefaultFilters(undefined)
      }, 0)
    }
  }, [defaultFilters, setDefaultFilters, setFilters])
  const { value: selectedOrganization } = useAsyncSelector(selectedMyOrganizationSelector)
  const organizationId = useMemo(() => selectedOrganization?.id, [selectedOrganization])
  const { value: organization } = useAsyncSelector(organizationDetailSelectorFamily(organizationId))
  const { value: myProfile } = useAsyncSelector(myProfileSelectorFamily(organizationId))
  const myMemberId = useMemo(() => myProfile?.memberId, [myProfile])
  const { refreshUnreadContactCount } = useBottomTabBadgeData()
  const [nextTokens, setNextTokens] = useState<string[]>([])
  // 組織切り替え時のリフレッシュ用（前回の組織IDを保持）
  const prevOrganizationIdRef = useRef<string | undefined>(undefined)
  // 組織切り替えが発生したかのフラグ（リフレッシュ関数が定義された後に実行するため）
  const [needsOrganizationSwitchRefresh, setNeedsOrganizationSwitchRefresh] = useState(false)
  useEffect(() => {
    // 非同期で実行してレンダリング中のstate更新を回避
    setTimeout(() => {
      setNextTokens([])
      // 組織が変わった場合（初回のundefined→idは除く）
      if (prevOrganizationIdRef.current !== undefined && prevOrganizationIdRef.current !== organizationId) {
        console.log("[useInboxData] 組織切り替えを検出、楽観的状態をクリアします", {
          from: prevOrganizationIdRef.current,
          to: organizationId,
        })
        // 楽観的状態をクリア（別組織の既読状態が混ざるのを防ぐ）
        OptimisticStateService.clearAll()
        // リフレッシュ関数が定義された後に実行するためフラグをセット
        setNeedsOrganizationSwitchRefresh(true)
      }
      prevOrganizationIdRef.current = organizationId
    }, 0)
  }, [organizationId, setNextTokens])
  const setConfirmedSearchTextHelper = useCallback((text: string) => {
    setNextTokens([])
    setConfirmedSearchText(text)
  }, [])
  const params = useMemo(
    () => ({
      searchText: confirmedSearchText !== "" ? confirmedSearchText : undefined,
      filters: filters.length !== 0 ? filters : undefined,
      organizationId, // 組織切り替え時にセレクタを再評価させるため
    }),
    [confirmedSearchText, filters, organizationId]
  )
  const { value: getContacts, isLoading: isLoadingContacts } = useAsyncSelector(
    inboxContactsConcatSelectorFamily({ ...params, nextTokens })
  )
  const [contacts, setContacts] = useState<GetInboxContactsSuccess>()

  // 特定の連絡を既読にする（楽観的更新）
  // 未読バッジの減算もここで行う（ContactDetailScreen との二重減算を防ぐため、キャッシュを先に更新）
  const markContactAsReadInList = useCallback(
    (contactId: string) => {
      // OptimisticStateServiceに楽観的既読を追加（同期的に実行）
      OptimisticStateService.markAsRead(contactId)

      // 未読バッジを減算（キャッシュ更新前に、現在の未読状態をチェック）
      if (myMemberId && organizationId) {
        const wasUnread = ContactListCacheService.isContactUnreadInCache({
          teamMemberId: myMemberId,
          contactId,
        })
        // キャッシュ上で未読だった場合のみ減算
        if (wasUnread === true) {
          UnreadContactCountCacheService.decrementOptimistically(organizationId, 1)
          refreshUnreadContactCount()
        }
      }

      // キャッシュを既読に更新（同期的に実行）
      // これにより、ContactDetailScreen の isContactUnreadInCache が false を返し、二重減算を防ぐ
      if (myMemberId) {
        ContactListCacheService.markContactAsRead({ teamMemberId: myMemberId, contactId })
      }

      // React stateを更新（非同期で実行してレンダリング中のstate更新を回避）
      setTimeout(() => {
        setContacts((prev) => {
          if (!prev || !prev.contacts) return prev

          return {
            ...prev,
            contacts: prev.contacts.map((contact) => (contact.id === contactId ? { ...contact, isRead: true } : contact)),
          }
        })
      }, 0)
    },
    [myMemberId, organizationId, refreshUnreadContactCount]
  )
  const [favoriteFlgsCache, setFavoriteFlgsCache] = useRecoilState(inboxContactFavoriteFlgsCacheState)

  // totalをもとに次のページが存在するかどうかを判定
  // NextTokenは最後のページのレスポンスにも含まれてしまう
  // そのため、totalとcontactsの数を比較して次のページが存在するかどうかを判定する
  const hasNextPage = useMemo(() => {
    if (!getContacts) return false

    // totalがある場合はtotalで判定（OpenSearch）
    if (getContacts.total != null) {
      const currentContactsCount = getContacts?.contacts?.length || 0
      return currentContactsCount < getContacts.total
    }

    // totalがない場合はnextTokenの有無で判定（DynamoDB）
    // DynamoDBでは全体件数を効率的に取得できないため、nextTokenの有無で判定する
    return !!getContacts.nextToken
  }, [getContacts])

  const canLoadMore = useMemo(() => {
    return hasNextPage && !!getContacts?.nextToken
  }, [hasNextPage, getContacts?.nextToken])

  useEffect(() => {
    // 非同期で実行してレンダリング中のstate更新を回避
    setTimeout(() => {
      if (getContacts) {
        // 楽観的既読状態をマージ: 既にisReadがtrueの連絡は維持
        // これにより、フィルター切り替え時もAPIからの古いisRead: falseで上書きされない
        const mergeOptimisticReadState = (contactsList: typeof getContacts.contacts) => {
          return contactsList.map((contact) => {
            // OptimisticStateServiceを使用して楽観的既読をチェック
            if (OptimisticStateService.isOptimisticallyRead(contact.id)) {
              return { ...contact, isRead: true }
            }
            return contact
          })
        }

        // 「未読」フィルタ適用時は、楽観的に既読マークした連絡を除外
        // バックエンドはまだ未読として認識しているため、フロントエンドでフィルタリング
        const filterOutOptimisticallyRead = (contactsList: typeof getContacts.contacts) => {
          if (!filters.includes("unread")) {
            return contactsList
          }
          return contactsList.filter((contact) => {
            // OptimisticStateServiceを使用して楽観的既読をチェック
            if (OptimisticStateService.isOptimisticallyRead(contact.id)) {
              console.log("[useInboxData] 未読フィルタ: 楽観的既読の連絡を除外", { contactId: contact.id })
              return false
            }
            return true
          })
        }

        // 「お気に入り」フィルタ適用時は、楽観的にお気に入りにした連絡を追加
        // バックエンドはまだお気に入りとして認識していないため、フロントエンドで追加
        const addOptimisticallyFavorited = (contactsList: typeof getContacts.contacts) => {
          if (!filters.includes("favorite")) {
            return contactsList
          }

          // 既存のIDセット
          const existingIds = new Set(contactsList.map((c) => c.id))

          // OptimisticStateServiceから楽観的お気に入りを取得
          const optimisticallyFavorited = OptimisticStateService.getAllOptimisticallyFavoritedContacts()

          // 楽観的お気に入りの連絡を追加（まだリストにないもの）
          const contactsToAdd = optimisticallyFavorited.filter((contact) => {
            // favoriteFlgsCacheでお気に入り解除されていないことを確認
            return !existingIds.has(contact.id) && favoriteFlgsCache?.[contact.id] !== false
          })

          contactsToAdd.forEach((contact) => {
            console.log("[useInboxData] お気に入りフィルタ: 楽観的お気に入りを追加", { contactId: contact.id })
          })

          if (contactsToAdd.length === 0) {
            return contactsList
          }

          // 送信日時でソート（新しい順）して結合
          const combined = [...contactsList, ...contactsToAdd]
          return combined.sort((a, b) => {
            const dateA = a.sentDate ? new Date(a.sentDate).getTime() : 0
            const dateB = b.sentDate ? new Date(b.sentDate).getTime() : 0
            return dateB - dateA
          })
        }

        if (filters.includes("favorite") && getContacts.contacts) {
          const newContacts = getContacts.contacts.filter((e) => e.id && favoriteFlgsCache?.[e.id] !== false)
          const withOptimisticFavorites = addOptimisticallyFavorited(newContacts)
          const filteredContacts = filterOutOptimisticallyRead(withOptimisticFavorites)
          setContacts({
            contacts: mergeOptimisticReadState(filteredContacts),
            nextToken: getContacts.nextToken,
            total: getContacts.total,
          })
        } else {
          const filteredContacts = filterOutOptimisticallyRead(getContacts.contacts)
          setContacts({
            ...getContacts,
            contacts: mergeOptimisticReadState(filteredContacts),
          })
        }
      }
    }, 0)
  }, [getContacts, filters, favoriteFlgsCache])

  // 連絡一覧取得後、未読返信数をバッチで取得してキャッシュに保存
  const requestData = useRecoilValue(requestDataSelector)
  useEffect(() => {
    // 非同期で実行してレンダリング中のstate更新を回避
    setTimeout(() => {
      const fetchAndCacheUnreadCounts = async () => {
        if (!getContacts?.contacts || !requestData) return

        const contactIds = getContacts.contacts.map((c) => c.id).filter(Boolean)
        if (contactIds.length === 0) return

        try {
          const result = await getUnreadThreadCount({
            accessToken: requestData.accessToken,
            accountId: requestData.accountId,
            contactIds,
          })

          // 各連絡の未読数をキャッシュに保存
          const counts: Record<string, number> = {}
          result.buckets.forEach((bucket: { key?: string; doc_count?: number }) => {
            if (bucket.key) {
              counts[bucket.key] = bucket.doc_count ?? 0
            }
          })

          UnreadReplyCountCacheService.setBatch(counts)
          console.log("[useInboxData] Cached unread reply counts", {
            totalContacts: contactIds.length,
            cachedCounts: Object.keys(counts).length,
          })
        } catch (error) {
          console.error("[useInboxData] Failed to fetch unread counts", error)
        }
      }

      // バックグラウンドで実行（表示をブロックしない）
      fetchAndCacheUnreadCounts()
    }, 0)
  }, [getContacts?.contacts, requestData])

  const _refreshContacts = useRefresher(inboxContactsRequestIdState)
  const _refreshContactsConcat = useResetRecoilState(inboxContactsConcatRequestIdState)
  const _resetFavoriteFlgsCache = useResetRecoilState(inboxContactFavoriteFlgsCacheState)
  const refreshContactReplyThreads = useRefresher(contactReplyThreadsRequestIdState)
  const refreshUnreadThreadCount = useRefresher(unreadThreadCountRequestIdState)
  const refreshHasUnreadThreadMessage = useRefresher(hasUnreadThreadMessageRequestIdState)
  const refreshOrganizationDetail = useRefresher(organizationDetailRequestIdState)
  const needRefreshOrganization = useRecoilValue(organizationRefreshState)
  useEffect(() => {
    if (needRefreshOrganization) {
      // 非同期で実行してレンダリング中のstate更新を回避
      setTimeout(() => {
        refreshOrganizationDetail()
      }, 0)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needRefreshOrganization])

  // 組織切り替え時にRecoilセレクタを強制的に再評価
  // これにより、前の組織の楽観的既読状態が反映されたキャッシュが破棄される
  useEffect(() => {
    if (needsOrganizationSwitchRefresh) {
      console.log("[useInboxData] 組織切り替え後のセレクタ再評価を実行")
      _refreshContacts()
      _refreshContactsConcat()
      setNeedsOrganizationSwitchRefresh(false)
    }
  }, [needsOrganizationSwitchRefresh, _refreshContacts, _refreshContactsConcat])

  const refreshPermission = useRefresher(teamPermissionRequestIdState)

  // Pull-to-Refresh時に使用（キャッシュをクリア）- 内部実装
  const refreshContactsInternal = useCallback(() => {
    // Pull-to-Refresh時はキャッシュを明示的にクリア
    if (myProfile?.memberId) {
      ContactListCacheService.invalidateContainer(myProfile.memberId, "inbox")
    }

    setNextTokens([])
    _refreshContacts()
    _refreshContactsConcat()
    _resetFavoriteFlgsCache()
    refreshUnreadContactCount()
    refreshContactReplyThreads()
    refreshUnreadThreadCount()
    refreshHasUnreadThreadMessage()
    refreshOrganizationDetail()
    refreshPermission()
  }, [
    myProfile?.memberId,
    setNextTokens,
    _refreshContacts,
    _refreshContactsConcat,
    _resetFavoriteFlgsCache,
    refreshUnreadContactCount,
    refreshContactReplyThreads,
    refreshUnreadThreadCount,
    refreshHasUnreadThreadMessage,
    refreshOrganizationDetail,
    refreshPermission,
  ])

  // 画面フォーカス時に使用（キャッシュをクリアせずに更新）
  const refreshContactsOnFocus = useCallback(() => {
    // キャッシュは維持したまま、最新データを取得
    _refreshContacts()
    _refreshContactsConcat()
  }, [_refreshContacts, _refreshContactsConcat])

  const refreshFiltersContacts = useCallback(() => {
    setNextTokens([])
    _refreshContactsConcat()
  }, [_refreshContactsConcat])

  const setFiltersHelper = useCallback(
    (filters: ContactFilterValue[]) => {
      setFilters(filters)
      // フィルタ変更時は必ずnextTokensをリセット（ページング状態をクリア）
      // お気に入りフィルタも含めて、すべてのフィルタ変更で1ページ目から取得する
      refreshFiltersContacts()
    },
    [refreshFiltersContacts]
  )

  const refreshContactDetailData = useContactDetailDataRefresher()

  const requestDataResult = useRequestData()

  const { fetch: execTrashContact } = useFetcher(
    useCallback(
      async (contactId: string) => {
        if (!requestDataResult.isOk) {
          return
        }
        const { accountId, accessToken } = requestDataResult.content
        if (accountId == null || accessToken == null || myMemberId == null) {
          return
        }
        const isConfirmed = await CustomAlert.confirm(
          "確認",
          "この連絡をゴミ箱へ移動しますか？\nこの連絡への返信も確認できなくなります。"
        )
        if (!isConfirmed) {
          return
        }

        // 移動する連絡が未読かどうかを確認（バッジ更新用）
        const targetContact = contacts?.contacts?.find((c) => c.id === contactId)
        const wasUnread = targetContact != null && !targetContact.isRead

        const result = await trashContact({ accessToken, myMemberId, contactId })
        if (result.isOk) {
          // ゴミ箱のキャッシュを無効化（移動した連絡を即座に表示）
          if (myMemberId) {
            ContactListCacheService.invalidateContainer(myMemberId, "trashbox")
          }

          // 未読連絡をゴミ箱に移動した場合、未読バッジを更新
          if (wasUnread && organizationId) {
            UnreadContactCountCacheService.decrementOptimistically(organizationId, 1)
            refreshUnreadContactCount()
          }

          await CustomAlert.alert("完了", "ゴミ箱に移動しました。")
          await wait(2000)
          refreshContactsInternal()
        } else {
          await CustomAlert.alert("エラー", result.error.message)
        }
      },
      [requestDataResult, refreshContactsInternal, myMemberId, contacts?.contacts, organizationId, refreshUnreadContactCount]
    )
  )

  const { fetch: execUpdateFavoriteContact } = useFetcher(
    useCallback(
      async ({ contactId, isFavorite }: { contactId: string; isFavorite: boolean }) => {
        if (!requestDataResult.isOk) {
          return
        }
        const { accountId, accessToken } = requestDataResult.content
        if (accountId == null || accessToken == null || myMemberId == null) {
          return
        }
        const result = await updateFavoriteContactInbox({ accessToken, myMemberId, contactId, isFavorite })
        if (!result.isOk) {
          CustomAlert.alert("エラー", result.error.message)
          return
        }
        setFavoriteFlgsCache((prev) => ({ ...prev, [contactId]: isFavorite }))

        // OptimisticStateServiceを使用して楽観的お気に入りを管理
        if (isFavorite) {
          // お気に入りにした場合、連絡データを保存（フィルタ切り替え時に追加するため）
          const targetContact = contacts?.contacts?.find((c) => c.id === contactId)
          if (targetContact) {
            OptimisticStateService.setFavorited(contactId, targetContact)
          }
        } else {
          // お気に入り解除した場合、保存データを削除
          OptimisticStateService.removeFavorited(contactId)
        }

        // キャッシュ内のお気に入り状態も更新
        ContactListCacheService.updateContactFavorite({
          teamMemberId: myMemberId,
          contactId,
          isFavorite,
        })
        // 連絡詳細キャッシュも無効化（古い状態が残らないように）
        ContactListCacheService.deleteDetailByContactId(contactId)
        if (!isFavorite && result.isOk && filters.includes("favorite") && contacts && contacts.contacts) {
          const newContacts = contacts.contacts.filter((e: { id: string }) => e.id && e.id !== contactId)
          setContacts({
            contacts: newContacts,
            nextToken: contacts.nextToken,
          })
        }
      },
      [requestDataResult, myMemberId, setFavoriteFlgsCache, filters, contacts]
    )
  )

  const resetCreateNewContactScreenData = useResetter()
  const request = useRecoilValue(requestDataSelector)

  // 増分的な未読数取得を使用
  const { unreadPerContact, resetUnreadCounts, resetProcessedContactsOnly, forceRefreshUnreadCounts, markContactAsRead } =
    useIncrementalUnreadCount({
      contacts,
      request: request || null,
    })

  // フィルタや検索変更時は楽観的既読を保持したまま、処理済みIDのみリセット
  useEffect(() => {
    // 非同期で実行してレンダリング中のstate更新を回避
    setTimeout(() => {
      resetProcessedContactsOnly()
    }, 0)
  }, [confirmedSearchText, filters, resetProcessedContactsOnly])

  // Pull-to-Refresh時に使用（キャッシュをクリア + 未読数をリセット）
  const refreshContacts = useCallback(() => {
    // OptimisticStateServiceの楽観的状態を完全にクリア（Pull-to-Refreshでサーバーの最新状態を反映）
    // メモリ + MMKVの両方をクリアして、サーバーの最新未読状態を取得
    OptimisticStateService.clearAllCompletely()
    console.log("[useInboxData] Pull-to-Refresh: 楽観的状態を完全にクリア")

    // 未読数の状態をリセット（processedContactIdsもクリア）
    // これにより、連絡一覧が更新された後にuseEffectが自動的に未読数を再取得する
    resetUnreadCounts()
    refreshContactsInternal()
  }, [refreshContactsInternal, resetUnreadCounts])

  // バックグラウンド復帰時の条件付きリフレッシュ
  // - 最後のリフレッシュから一定時間（DEV: 1分、STG/PROD: 3分）経過した場合のみチェック
  // - 新しい連絡がある場合のみキャッシュを破棄してリフレッシュ
  // - 新しい連絡がない場合はキャッシュを維持（コスト抑制）
  const tokens = useRecoilValue(tokensState)
  const checkForNewData = useCallback(async (): Promise<boolean> => {
    if (!tokens?.accessToken || !myMemberId) {
      return false
    }

    // キャッシュの最新連絡を取得
    const cachedContacts = contacts?.contacts
    if (!cachedContacts || cachedContacts.length === 0) {
      // キャッシュが空の場合は新しいデータがあるとみなす
      return true
    }

    const cachedLatest = cachedContacts[0]

    // APIから最新の連絡を1件取得
    const latestFromApi = await checkLatestInboxContact({
      accessToken: tokens.accessToken,
      myMemberId,
    })

    if (!latestFromApi) {
      // APIエラーまたは連絡がない場合はリフレッシュしない
      return false
    }

    // 最新の連絡IDが異なる場合は新しい連絡がある
    const hasNewData = latestFromApi.id !== cachedLatest.id
    if (hasNewData) {
      console.log("[useInboxData] New contact detected", {
        cachedLatestId: cachedLatest.id,
        apiLatestId: latestFromApi.id,
      })
    }

    return hasNewData
  }, [tokens?.accessToken, myMemberId, contacts?.contacts])

  const onForegroundRefresh = useCallback(() => {
    // キャッシュをクリアして最新データを取得
    if (myProfile?.memberId) {
      ContactListCacheService.invalidateContainer(myProfile.memberId, "inbox")
    }

    // 連絡一覧と未読バッジを更新
    resetUnreadCounts()
    _refreshContacts()
    _refreshContactsConcat()
    refreshUnreadContactCount()
    refreshHasUnreadThreadMessage()
  }, [
    myProfile?.memberId,
    resetUnreadCounts,
    _refreshContacts,
    _refreshContactsConcat,
    refreshUnreadContactCount,
    refreshHasUnreadThreadMessage,
  ])

  const { markRefreshed } = useForegroundRefresh({
    checkForNewData,
    onRefresh: onForegroundRefresh,
    screenName: "InboxScreen",
  })

  // アプリ起動時（メモリにない状態から）の新着チェック
  // バックグラウンド復帰時と同じロジックだが、時間チェックは不要
  const hasCheckedOnLaunch = useRef(false)
  useEffect(() => {
    // Web版では無効
    if (Platform.OS === "web") return

    // 既にチェック済みの場合はスキップ
    if (hasCheckedOnLaunch.current) return

    // アプリ起動フラグが立っていない場合はスキップ
    if (!getIsAppJustLaunched()) return

    // APIアクセスに必要なデータが揃っていない場合はスキップ
    if (!tokens?.accessToken || !myMemberId) return

    const checkOnAppLaunch = async () => {
      hasCheckedOnLaunch.current = true
      clearAppJustLaunchedFlag()

      console.log("[useInboxData] App launched - checking for new data")

      try {
        // キャッシュの最新連絡を取得
        const cachedData = ContactListCacheService.get({
          teamMemberId: myMemberId,
          containerType: "inbox",
          filters: [], // デフォルトフィルタで最初のページを取得
        })

        if (!cachedData?.contacts || cachedData.contacts.length === 0) {
          // キャッシュが空の場合は新しいデータがあるとみなす
          console.log("[useInboxData] App launch - no cached data, refreshing")
          onForegroundRefresh()
          return
        }

        const cachedLatest = cachedData.contacts[0]

        // APIから最新の連絡を1件取得
        const latestFromApi = await checkLatestInboxContact({
          accessToken: tokens.accessToken,
          myMemberId,
        })

        if (!latestFromApi) {
          // APIエラーまたは連絡がない場合はキャッシュを維持
          console.log("[useInboxData] App launch - API error or no data, keeping cache")
          return
        }

        // 最新の連絡IDが異なる場合は新しい連絡がある
        if (latestFromApi.id !== cachedLatest.id) {
          console.log("[useInboxData] App launch - new contact detected, refreshing", {
            cachedLatestId: cachedLatest.id,
            apiLatestId: latestFromApi.id,
          })
          onForegroundRefresh()
        } else {
          console.log("[useInboxData] App launch - no new data, keeping cache")
        }
      } catch (error) {
        console.error("[useInboxData] Error checking for new data on app launch", error)
      }
    }

    checkOnAppLaunch()
  }, [tokens?.accessToken, myMemberId, onForegroundRefresh])

  // Pull-to-Refresh時にも最終リフレッシュ時刻を更新
  const refreshContactsWithMark = useCallback(() => {
    markRefreshed()
    refreshContacts()
  }, [markRefreshed, refreshContacts])

  return {
    searchText,
    setSearchText: setSearchText,
    confirmedSearchText,
    setConfirmedSearchText: setConfirmedSearchTextHelper,
    filters,
    setFilters: setFiltersHelper,
    contacts,
    isLoadingContacts,
    unreadPerContact,
    refreshContacts: refreshContactsWithMark,
    organizationId,
    myMemberId,
    refreshContactDetailData,
    execTrashContact,
    execUpdateFavoriteContact,
    resetCreateNewContactScreenData,
    setNextTokens,
    favoriteFlgsCache,
    organization,
    canLoadMore,
    markContactAsRead,
    markContactAsReadInList,
  }
}
